import { useState } from "react";
import { useParams } from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import {
  usePublicStoreBranding,
  useCreatePublicReturn,
  MAX_RETURN_ITEMS,
  type RequestType,
  type PublicProductCandidate,
} from "@/hooks/use-returns";
import { PublicProductPicker } from "@/components/returns/public-product-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { readableAccent } from "@/lib/accent";

const REQUEST_TYPE_OPTIONS: { value: RequestType; label: string; hint: string }[] = [
  { value: "mijenja", label: "Mijenja", hint: "Zamjena za drugu veličinu/model" },
  { value: "vraca", label: "Vraća", hint: "Povrat novca na žiro račun" },
  { value: "mijenja_dodatno", label: "Mijenja + naručuje dodatno", hint: "Zamjena i nova narudžba" },
];

const REASON_OPTIONS: { value: string; label: string }[] = [
  { value: "preveliko", label: "Preveliko" },
  { value: "premalo", label: "Premalo" },
  { value: "kvalitet", label: "Kvalitet proizvoda" },
  { value: "razlikuje_se", label: "Razlikuje se od proizvoda na slici" },
  { value: "sa_greskom", label: "Proizvod sa greškom" },
  { value: "kasna_isporuka", label: "Kasna isporuka" },
  { value: "ne_stoji", label: "Ne stoji mi dobro" },
];

// ── Formatters ───────────────────────────────────────────────────────────────

// Strip non-digits, cap at 16, render as XXXX XXXX XXXX XXXX as the user types.
function formatBankAccount(input: string): string {
  const digits = input.replace(/\D/g, "").slice(0, 16);
  return digits.replace(/(.{4})/g, "$1 ").trim();
}
function bankAccountDigits(input: string): string {
  return input.replace(/\D/g, "");
}

// BiH mobile: +387 XX XXX XXX(X) — 8 or 9 local digits. Strip country-code
// prefix if user pastes "+387 ", "00387", or a leading "0", then rebuild mask.
// 8-digit:  +387 XX XXX XXX
// 9-digit:  +387 XX XXX XXXX
function formatPhone(input: string): string {
  let digits = input.replace(/\D/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.startsWith("387")) digits = digits.slice(3);
  if (digits.startsWith("0")) digits = digits.slice(1);
  digits = digits.slice(0, 9);
  if (!digits) return "";
  const out = ["+387"];
  out.push(digits.slice(0, 2));
  if (digits.length > 2) out.push(digits.slice(2, 5));
  if (digits.length > 5) out.push(digits.slice(5, 9));
  return out.join(" ");
}
function phoneLocalDigits(input: string): string {
  let digits = input.replace(/\D/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.startsWith("387")) digits = digits.slice(3);
  if (digits.startsWith("0")) digits = digits.slice(1);
  return digits;
}

// ── Validators (each returns a Bosnian error string, or undefined when ok) ──

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
// Latin + BiH diacritics + cyrillic; must start with a letter; allow space, hyphen, apostrophe.
const NAME_RE = /^[A-Za-zÀ-ÿČčĆćĐđŠšŽž][A-Za-zÀ-ÿČčĆćĐđŠšŽž '\-]*$/;

const v = {
  required: (val: string) => (!val || !val.trim() ? "Obavezno polje" : undefined),
  name: (val: string) => {
    const t = (val || "").trim();
    if (!t) return "Obavezno polje";
    if (t.length < 2) return "Najmanje 2 znaka";
    if (t.length > 60) return "Najviše 60 znakova";
    if (!NAME_RE.test(t)) return "Samo slova, razmaci i crtica";
    return undefined;
  },
  email: (val: string) => {
    const t = (val || "").trim();
    if (!t) return "Obavezno polje";
    if (t.length > 120) return "Email predug";
    if (!EMAIL_RE.test(t)) return "Neispravan email format";
    return undefined;
  },
  phone: (val: string) => {
    const local = phoneLocalDigits(val || "");
    if (!local) return "Obavezno polje";
    if (local.length < 8) return "Premalo cifri (8 ili 9)";
    if (local.length > 9) return "Previše cifri (8 ili 9)";
    return undefined;
  },
  orderNumber: (val: string) => {
    if (!val) return undefined; // optional
    if (val.length > 30) return "Najviše 30 znakova";
    return undefined;
  },
  shortText: (val: string) => {
    const t = (val || "").trim();
    if (!t) return "Obavezno polje";
    if (t.length > 80) return "Najviše 80 znakova";
    return undefined;
  },
  size: (val: string) => {
    const t = (val || "").trim();
    if (!t) return "Obavezno polje";
    if (t.length > 12) return "Najviše 12 znakova";
    return undefined;
  },
  postalCode: (val: string) => {
    const t = (val || "").trim();
    if (!t) return "Obavezno polje";
    if (t.length > 10) return "Najviše 10 znakova";
    return undefined;
  },
  bankAccount: (val: string) => {
    const digits = bankAccountDigits(val || "");
    if (!digits) return "Obavezno polje";
    if (digits.length < 16) return `Žiro račun mora imati 16 cifri (uneseno ${digits.length})`;
    if (digits.length > 16) return "Žiro račun ima najviše 16 cifri";
    return undefined;
  },
};

// ── UI helpers ───────────────────────────────────────────────────────────────

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-foreground/10 pb-2">
      <span className="text-[11px] font-medium uppercase tracking-[0.18em]">{title}</span>
      {subtitle && <span className="text-[10px] text-muted-foreground">{subtitle}</span>}
    </div>
  );
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground gap-1">
      {children}
      {required && <span className="text-destructive">*</span>}
    </Label>
  );
}

function FieldError({ errors, isTouched }: { errors: any[]; isTouched: boolean }) {
  if (!isTouched || !errors.length) return null;
  return <p className="text-[10px] text-destructive mt-0.5">{String(errors[0])}</p>;
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function PublicReturnFormPage() {
  const { slug } = useParams({ strict: false });
  const { data: store, isLoading, isError } = usePublicStoreBranding(slug ?? null);
  const createReturn = useCreatePublicReturn();
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const form = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      orderNumber: "",
      requestType: "mijenja" as RequestType,
      // Stavka je definisana odabranim proizvodom — model, boja i veličina se izvode iz njega.
      items: [{ link: null, exchangeLink: null }] as Array<{
        link: PublicProductCandidate | null;
        exchangeLink: PublicProductCandidate | null;
      }>,
      additionalItems: [{ link: null }] as Array<{ link: PublicProductCandidate | null }>,
      reasons: [] as string[],
      declaration: false,
      bankAccount: "",
      deliveryAddress: "",
      deliveryCity: "",
      deliveryPostalCode: "",
    },
    onSubmit: async ({ value }) => {
      setErrorMsg(null);
      try {
        const isExchange =
          value.requestType === "mijenja" || value.requestType === "mijenja_dodatno";
        const items = value.items.map((it) =>
          isExchange ? { link: it.link, exchangeLink: it.exchangeLink } : { link: it.link },
        );
        const additionalItems =
          value.requestType === "mijenja_dodatno"
            ? value.additionalItems.map((it) => ({ link: it.link }))
            : [];
        const result = await createReturn.mutateAsync({
          slug: slug!,
          firstName: value.firstName,
          lastName: value.lastName,
          email: value.email,
          phone: formatPhone(value.phone),
          orderNumber: value.orderNumber,
          requestType: value.requestType,
          items,
          additionalItems,
          reasons: value.reasons,
          declaration: value.declaration,
          bankAccount:
            value.requestType === "vraca" ? bankAccountDigits(value.bankAccount) : "",
          deliveryAddress: isExchange ? value.deliveryAddress : "",
          deliveryCity: isExchange ? value.deliveryCity : "",
          deliveryPostalCode: isExchange ? value.deliveryPostalCode : "",
        });
        setSubmitted(result.referenceNumber);
      } catch (err: any) {
        setErrorMsg(err.message || "Greška pri slanju zahtjeva");
      }
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !store) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="ring-1 ring-foreground/10 px-8 py-6 text-center max-w-sm">
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Greška</p>
          <p className="text-sm">Forma za povrat nije dostupna.</p>
        </div>
      </div>
    );
  }

  const accent = readableAccent(store.primaryColor, "#b54a2b");

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-md ring-1 ring-foreground/10 bg-card">
          <div className="border-b border-foreground/10 px-6 py-3 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Status</span>
            <span className="text-[10px] uppercase tracking-[0.2em] flex items-center gap-1.5" style={{ color: accent }}>
              <CheckCircle2 className="h-3 w-3" /> Zaprimljeno
            </span>
          </div>
          <div className="p-6 text-center space-y-4">
            <h1 className="text-base font-medium">Zahtjev je zaprimljen</h1>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Vaš zahtjev je uspješno poslan trgovini. Sačuvajte referentni broj za praćenje statusa.
            </p>
            <div className="flex flex-col items-center gap-1.5 pt-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Referentni broj</span>
              <span className="font-mono text-2xl tracking-[0.15em] tabular-nums" style={{ color: accent }}>
                {submitted}
              </span>
            </div>
          </div>
          <div className="border-t border-foreground/10 px-6 py-3">
            <a
              href={`/return/${slug}/status/${submitted}`}
              className="flex items-center justify-between text-xs hover:opacity-80 transition-opacity"
              style={{ color: accent }}
            >
              <span className="uppercase tracking-[0.14em]">Provjeri status</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  const accentStyle = { ["--accent" as any]: accent } as React.CSSProperties;

  return (
    <div
      className="min-h-screen bg-background py-8 px-4 sm:py-12"
      style={{ ...accentStyle, ["--tw-ring-color" as any]: accent }}
    >
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <header className="ring-1 ring-foreground/10 bg-card mb-3">
          <div className="flex flex-col items-center gap-4 px-6 py-8">
            <div className="flex items-center justify-center size-28 sm:size-32 overflow-hidden bg-muted/30">
              {store.logoUrl ? (
                <img src={store.logoUrl} alt={store.name} className="w-full h-full object-contain" />
              ) : (
                <span className="text-3xl font-semibold" style={{ color: accent }}>
                  {store.name.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex flex-col items-center gap-1 text-center">
              <h1 className="text-base font-medium">{store.name}</h1>
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Zahtjev za povrat ili zamjenu
              </p>
            </div>
          </div>
        </header>

        <div className="ring-1 ring-foreground/10 bg-muted/30 px-5 py-3 mb-3 grid sm:grid-cols-2 gap-1 sm:gap-4 text-[11px]">
          <p>Povrat/zamjena nisu mogući bez fiskalnog računa.</p>
          <p>Rok za zamjenu je 15 dana. Troškove dostave snosi kupac.</p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className="ring-1 ring-foreground/10 bg-card divide-y divide-foreground/10"
        >
          {/* Section 1: Customer */}
          <section className="p-5 sm:p-6 space-y-4">
            <SectionHeader title="Podaci o kupcu" subtitle="Vaše ime, kontakt informacije i broj narudžbe" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <form.Field name="firstName" validators={{ onChange: ({ value }) => v.name(value), onBlur: ({ value }) => v.name(value) }}>
                {(field) => (
                  <div className="space-y-1.5">
                    <FieldLabel required>Vaše ime</FieldLabel>
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder="Emina"
                      aria-invalid={field.state.meta.isTouched && field.state.meta.errors.length > 0}
                    />
                    <FieldError errors={field.state.meta.errors} isTouched={field.state.meta.isTouched} />
                  </div>
                )}
              </form.Field>

              <form.Field name="lastName" validators={{ onChange: ({ value }) => v.name(value), onBlur: ({ value }) => v.name(value) }}>
                {(field) => (
                  <div className="space-y-1.5">
                    <FieldLabel required>Vaše prezime</FieldLabel>
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder="Begić"
                      aria-invalid={field.state.meta.isTouched && field.state.meta.errors.length > 0}
                    />
                    <FieldError errors={field.state.meta.errors} isTouched={field.state.meta.isTouched} />
                  </div>
                )}
              </form.Field>

              <form.Field name="email" validators={{ onChange: ({ value }) => v.email(value), onBlur: ({ value }) => v.email(value) }}>
                {(field) => (
                  <div className="space-y-1.5">
                    <FieldLabel required>Vaš email</FieldLabel>
                    <Input
                      type="email"
                      inputMode="email"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder="emina.begic@gmail.com"
                      aria-invalid={field.state.meta.isTouched && field.state.meta.errors.length > 0}
                    />
                    <FieldError errors={field.state.meta.errors} isTouched={field.state.meta.isTouched} />
                  </div>
                )}
              </form.Field>

              <form.Field name="phone" validators={{ onChange: ({ value }) => v.phone(value), onBlur: ({ value }) => v.phone(value) }}>
                {(field) => (
                  <div className="space-y-1.5">
                    <FieldLabel required>Vaš broj telefona</FieldLabel>
                    <Input
                      type="tel"
                      inputMode="tel"
                      value={field.state.value}
                      // Reformat as user types — strip country code prefixes, rebuild +387 XX XXX XXXX.
                      onChange={(e) => field.handleChange(formatPhone(e.target.value))}
                      onBlur={field.handleBlur}
                      placeholder="+387 61 234 5678"
                      className="font-mono"
                      aria-invalid={field.state.meta.isTouched && field.state.meta.errors.length > 0}
                    />
                    <FieldError errors={field.state.meta.errors} isTouched={field.state.meta.isTouched} />
                  </div>
                )}
              </form.Field>

              <form.Field name="orderNumber" validators={{ onChange: ({ value }) => v.orderNumber(value) }}>
                {(field) => (
                  <div className="space-y-1.5 sm:col-span-2">
                    <FieldLabel>Vaš broj narudžbe (opcionalno)</FieldLabel>
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder="#54821"
                      className="font-mono"
                      maxLength={30}
                      aria-invalid={field.state.meta.isTouched && field.state.meta.errors.length > 0}
                    />
                    <FieldError errors={field.state.meta.errors} isTouched={field.state.meta.isTouched} />
                  </div>
                )}
              </form.Field>
            </div>
          </section>

          {/* Section 2: Request type */}
          <section className="p-5 sm:p-6 space-y-4">
            <SectionHeader title="Tip zahtjeva" subtitle="Odaberite šta želite uraditi sa proizvodom" />

            <form.Field name="requestType">
              {(field) => (
                <RadioGroup
                  value={field.state.value}
                  onValueChange={(val) => field.handleChange(val as RequestType)}
                  className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1"
                >
                  {REQUEST_TYPE_OPTIONS.map((opt) => {
                    const selected = field.state.value === opt.value;
                    return (
                      <label
                        key={opt.value}
                        className={cn(
                          "relative flex items-start gap-3 ring-1 ring-foreground/10 px-3 py-3 cursor-pointer transition-colors hover:bg-muted/30",
                          selected && "ring-2 bg-muted/30",
                        )}
                        style={selected ? { boxShadow: "inset 0 0 0 1px #000000" } : undefined}
                      >
                        <RadioGroupItem
                          value={opt.value}
                          className="mt-0.5"
                          style={{ borderColor: "#000000", color: "#000000" }}
                        />
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <span className="text-xs font-medium leading-tight">{opt.label}</span>
                          <span className="text-[10px] text-muted-foreground leading-snug">{opt.hint}</span>
                        </div>
                      </label>
                    );
                  })}
                </RadioGroup>
              )}
            </form.Field>
          </section>

          {/* Section 3: Product details (depend on request type) */}
          <form.Subscribe selector={(state) => state.values.requestType}>
            {(requestType) => {
              const isExchange = requestType === "mijenja" || requestType === "mijenja_dodatno";
              const isReturn = requestType === "vraca";

              return (
                <section className="p-5 sm:p-6 space-y-4">
                  <SectionHeader
                    title="Proizvodi"
                    subtitle={
                      isReturn
                        ? "Odaberite proizvode koje vraćate iz naše ponude"
                        : "Odaberite proizvode koje mijenjate i njihove zamjene"
                    }
                  />

                  <form.Field name="items" mode="array">
                    {(itemsField) => (
                      <div className="space-y-3">
                        {itemsField.state.value.map((_, i) => (
                          <div
                            key={i}
                            className="ring-1 ring-foreground/10 p-4 space-y-3 relative"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground">
                                PROIZVOD {i + 1}
                              </span>
                              {itemsField.state.value.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => itemsField.removeValue(i)}
                                  className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-destructive transition-colors"
                                >
                                  Ukloni
                                </button>
                              )}
                            </div>

                            <form.Field
                              name={`items[${i}].link`}
                              validators={{
                                onChange: ({ value }) => (value ? undefined : "Odaberite proizvod"),
                              }}
                            >
                              {(linkField) => (
                                <PublicProductPicker
                                  slug={slug!}
                                  accent={accent}
                                  label={isReturn ? "Proizvod koji vraćate" : "Proizvod koji mijenjate"}
                                  value={linkField.state.value as PublicProductCandidate | null}
                                  error={
                                    linkField.state.meta.isTouched
                                      ? (linkField.state.meta.errors[0] as string | undefined)
                                      : undefined
                                  }
                                  onSelect={(c) => linkField.handleChange(c as any)}
                                  onClear={() => linkField.handleChange(null as any)}
                                />
                              )}
                            </form.Field>

                            {isExchange && (
                              <>
                                <div className="flex items-center gap-3 pt-1">
                                  <Separator className="flex-1" />
                                  <span className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground">
                                    → ZAMJENA
                                  </span>
                                  <Separator className="flex-1" />
                                </div>
                                <form.Field
                                  name={`items[${i}].exchangeLink`}
                                  validators={{
                                    onChange: ({ value }) => (value ? undefined : "Odaberite zamjenski proizvod"),
                                  }}
                                >
                                  {(linkField) => (
                                    <PublicProductPicker
                                      slug={slug!}
                                      accent={accent}
                                      label="Zamjenski proizvod"
                                      inStockOnly
                                      value={linkField.state.value as PublicProductCandidate | null}
                                      error={
                                        linkField.state.meta.isTouched
                                          ? (linkField.state.meta.errors[0] as string | undefined)
                                          : undefined
                                      }
                                      onSelect={(c) => linkField.handleChange(c as any)}
                                      onClear={() => linkField.handleChange(null as any)}
                                    />
                                  )}
                                </form.Field>
                              </>
                            )}
                          </div>
                        ))}

                        <button
                          type="button"
                          disabled={itemsField.state.value.length >= MAX_RETURN_ITEMS}
                          onClick={() =>
                            itemsField.pushValue({ link: null, exchangeLink: null })
                          }
                          className="w-full ring-1 ring-foreground/10 py-2 text-[11px] uppercase tracking-[0.18em] hover:bg-muted/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ color: accent }}
                        >
                          + Dodaj još jedan proizvod
                        </button>
                      </div>
                    )}
                  </form.Field>

                  {requestType === "mijenja_dodatno" && (
                    <div className="space-y-4 pt-2">
                      <SectionHeader
                        title="Dodatno naručeni proizvodi"
                        subtitle="Proizvodi koje želite dodatno naručiti"
                      />
                      <form.Field name="additionalItems" mode="array">
                        {(addField) => (
                          <div className="space-y-3">
                            {addField.state.value.map((_, i) => (
                              <div
                                key={i}
                                className="ring-1 ring-foreground/10 p-4 space-y-3 relative"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground">
                                    DODATNO {i + 1}
                                  </span>
                                  {addField.state.value.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => addField.removeValue(i)}
                                      className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-destructive transition-colors"
                                    >
                                      Ukloni
                                    </button>
                                  )}
                                </div>
                                <form.Field
                                  name={`additionalItems[${i}].link`}
                                  validators={{
                                    onChange: ({ value }) =>
                                      value || i > 0 ? undefined : "Odaberite proizvod",
                                  }}
                                >
                                  {(linkField) => (
                                    <PublicProductPicker
                                      slug={slug!}
                                      accent={accent}
                                      label="Proizvod koji naručujete"
                                      inStockOnly
                                      value={linkField.state.value as PublicProductCandidate | null}
                                      error={
                                        linkField.state.meta.isTouched
                                          ? (linkField.state.meta.errors[0] as string | undefined)
                                          : undefined
                                      }
                                      onSelect={(c) => linkField.handleChange(c as any)}
                                      onClear={() => linkField.handleChange(null as any)}
                                    />
                                  )}
                                </form.Field>
                              </div>
                            ))}

                            <button
                              type="button"
                              disabled={addField.state.value.length >= MAX_RETURN_ITEMS}
                              onClick={() =>
                                addField.pushValue({ link: null })
                              }
                              className="w-full ring-1 ring-foreground/10 py-2 text-[11px] uppercase tracking-[0.18em] hover:bg-muted/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{ color: accent }}
                            >
                              + Dodaj dodatni proizvod
                            </button>
                          </div>
                        )}
                      </form.Field>
                    </div>
                  )}

                  {isReturn && (
                    <form.Field
                      name="bankAccount"
                      validators={{
                        onChange: ({ value }) => v.bankAccount(value),
                        onBlur: ({ value }) => v.bankAccount(value),
                      }}
                    >
                      {(field) => (
                        <div className="space-y-1.5 ring-1 ring-foreground/10 bg-muted/30 p-3">
                          <FieldLabel required>Vaš žiro račun za povrat novca</FieldLabel>
                          <Input
                            inputMode="numeric"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(formatBankAccount(e.target.value))}
                            onBlur={field.handleBlur}
                            placeholder="XXXX XXXX XXXX XXXX"
                            className="font-mono tracking-wider"
                            maxLength={19}
                            aria-invalid={field.state.meta.isTouched && field.state.meta.errors.length > 0}
                          />
                          <FieldError errors={field.state.meta.errors} isTouched={field.state.meta.isTouched} />
                          <p className="text-[10px] text-muted-foreground pt-1">
                            16 cifri. Iznos će biti vraćen na navedeni račun nakon odobrenja zahtjeva.
                          </p>
                        </div>
                      )}
                    </form.Field>
                  )}
                </section>
              );
            }}
          </form.Subscribe>

          {/* Section 3b: Delivery address (exchanges only — replacement is shipped here) */}
          <form.Subscribe selector={(state) => state.values.requestType}>
            {(requestType) => {
              const isExchange = requestType === "mijenja" || requestType === "mijenja_dodatno";
              if (!isExchange) return null;

              return (
                <section className="p-5 sm:p-6 space-y-4">
                  <SectionHeader
                    title="Vaša adresa dostave"
                    subtitle="Na ovu adresu šaljemo zamjenski artikal"
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    <form.Field
                      name="deliveryAddress"
                      validators={{ onChange: ({ value }) => v.shortText(value), onBlur: ({ value }) => v.shortText(value) }}
                    >
                      {(field) => (
                        <div className="space-y-1.5 sm:col-span-2">
                          <FieldLabel required>Vaša adresa (ulica i broj)</FieldLabel>
                          <Input
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            placeholder="Zmaja od Bosne 12"
                            aria-invalid={field.state.meta.isTouched && field.state.meta.errors.length > 0}
                          />
                          <FieldError errors={field.state.meta.errors} isTouched={field.state.meta.isTouched} />
                        </div>
                      )}
                    </form.Field>

                    <form.Field
                      name="deliveryCity"
                      validators={{ onChange: ({ value }) => v.shortText(value), onBlur: ({ value }) => v.shortText(value) }}
                    >
                      {(field) => (
                        <div className="space-y-1.5">
                          <FieldLabel required>Vaš grad</FieldLabel>
                          <Input
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            placeholder="Sarajevo"
                            aria-invalid={field.state.meta.isTouched && field.state.meta.errors.length > 0}
                          />
                          <FieldError errors={field.state.meta.errors} isTouched={field.state.meta.isTouched} />
                        </div>
                      )}
                    </form.Field>

                    <form.Field
                      name="deliveryPostalCode"
                      validators={{ onChange: ({ value }) => v.postalCode(value), onBlur: ({ value }) => v.postalCode(value) }}
                    >
                      {(field) => (
                        <div className="space-y-1.5">
                          <FieldLabel required>Vaš poštanski broj</FieldLabel>
                          <Input
                            inputMode="numeric"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            placeholder="71000"
                            className="font-mono"
                            maxLength={10}
                            aria-invalid={field.state.meta.isTouched && field.state.meta.errors.length > 0}
                          />
                          <FieldError errors={field.state.meta.errors} isTouched={field.state.meta.isTouched} />
                        </div>
                      )}
                    </form.Field>
                  </div>
                </section>
              );
            }}
          </form.Subscribe>

          {/* Section 4: Reasons */}
          <section className="p-5 sm:p-6 space-y-4">
            <SectionHeader title="Razlog povrata / zamjene" subtitle="Označite jednu ili više opcija" />

            <form.Field
              name="reasons"
              validators={{ onChange: ({ value }) => (value.length === 0 ? "Odaberite barem jedan razlog" : undefined) }}
            >
              {(field) => (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 pt-1">
                    {REASON_OPTIONS.map((opt) => {
                      const checked = field.state.value.includes(opt.value);
                      return (
                        <label
                          key={opt.value}
                          className={cn(
                            "flex items-center gap-2.5 ring-1 ring-foreground/10 px-3 py-2 cursor-pointer transition-colors hover:bg-muted/30 select-none",
                            checked && "bg-muted/40",
                          )}
                          style={checked ? { boxShadow: "inset 0 0 0 1px #000000" } : undefined}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => {
                              const next = checked
                                ? field.state.value.filter((r) => r !== opt.value)
                                : [...field.state.value, opt.value];
                              field.handleChange(next);
                            }}
                            style={checked ? { backgroundColor: "#000000", borderColor: "#000000" } : undefined}
                          />
                          <span className="text-xs">{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  <FieldError errors={field.state.meta.errors} isTouched={field.state.meta.isTouched || field.state.value.length === 0} />
                </>
              )}
            </form.Field>
          </section>

          {/* Section 5: Declaration + Submit */}
          <section className="p-5 sm:p-6 space-y-4">
            <SectionHeader title="Izjava i potvrda" />

            <form.Field
              name="declaration"
              validators={{ onChange: ({ value }) => (!value ? "Morate potvrditi izjavu" : undefined) }}
            >
              {(field) => (
                <>
                  <label className="flex items-start gap-3 ring-1 ring-foreground/10 p-3 cursor-pointer hover:bg-muted/30 transition-colors">
                    <Checkbox
                      checked={field.state.value}
                      onCheckedChange={(val) => field.handleChange(val === true)}
                      className="mt-0.5"
                      style={field.state.value ? { backgroundColor: "#000000", borderColor: "#000000" } : undefined}
                    />
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs font-medium">Potvrđujem ispravnost proizvoda</span>
                      <span className="text-[11px] text-muted-foreground leading-relaxed">
                        Proizvodi nemaju tragove nošenja i nisu oštećeni. Posjedujem fiskalni račun.
                      </span>
                    </div>
                  </label>
                  <FieldError errors={field.state.meta.errors} isTouched={field.state.meta.isTouched} />
                </>
              )}
            </form.Field>

            {store.returnInstructionUrl && (
              <div className="ring-1 ring-foreground/10">
                <div className="border-b bg-muted/50 px-3 py-2">
                  <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                    Uputstvo za pakovanje
                  </span>
                </div>
                <a
                  href={store.returnInstructionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-3 hover:bg-muted/30 transition-colors"
                >
                  <img
                    src={store.returnInstructionUrl}
                    alt="Uputstvo za pakovanje paketa"
                    loading="lazy"
                    className="w-full max-w-full h-auto"
                  />
                  <span className="mt-2 block text-[11px] text-muted-foreground">
                    Kliknite na sliku za prikaz u punoj veličini.
                  </span>
                </a>
              </div>
            )}

            {errorMsg && (
              <div className="ring-1 ring-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                {errorMsg}
              </div>
            )}

            <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting, state.isValid]}>
              {([canSubmit, isSubmitting, isValid]) => (
                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-[0.14em]">
                    Polja označena <span className="text-destructive">*</span> su obavezna
                    {!isValid && <span className="ml-1.5 text-destructive normal-case">— provjerite polja u greški</span>}
                  </p>
                  <Button
                    type="submit"
                    size="lg"
                    disabled={!canSubmit || isSubmitting || createReturn.isPending}
                    className="bg-black text-white hover:bg-black/80 hover:opacity-90 min-w-[180px]"
                  >
                    {(isSubmitting || createReturn.isPending) && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    <span className="uppercase tracking-[0.18em]">
                      {isSubmitting || createReturn.isPending ? "Šaljem…" : "Pošalji zahtjev"}
                    </span>
                    {!(isSubmitting || createReturn.isPending) && <ArrowRight className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              )}
            </form.Subscribe>
          </section>
        </form>

        <footer className="text-center mt-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          {store.name} · RMA Forma
        </footer>
      </div>
    </div>
  );
}
